import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Row, Col, Form } from "react-bootstrap";
import ImageUploading from "react-images-uploading";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import SelectDropdown from "../components-react/SelectDropdown";
import FileUploadComponent from "../common-components/FileUploadComponent";
import { selectToken } from "../auth/auth.selector";
import {
  commonPostPutApi,
  fetchApiData,
} from "../../redux/reducers/commonApiSlice";
import { uploadImage } from "../../redux/reducers/postApiSlice";
import { CloudUploadIcon, DeleteIcon } from "../../images/icons/icons";
import useCloudFrontUpload from "../../hooks/useCloudFrontUpload";
import { fetchExpenseCategoriesData } from "../../redux/hygiene-reducers/hygieneSlice";
import { fetchVendors } from "../../data/dropdownApiS";
import {
  expense_payment_options,
  expense_payee_options,
} from "../common-data/DropdowData";
import "../../styles/ticket-system/work-completion-modal.css";
import ModalHeadAndCloseV2 from "../payouts-wtf/common/ModalHeadAndCloseV2";

// Set app element for react-modal accessibility
if (typeof window !== "undefined") {
  Modal.setAppElement("#root");
}

const RESOLUTION_TYPES = [
  { label: "Internal Resolution (by WTF Team)", value: "internal" },
  { label: "Paid External Service", value: "paid" },
];

const WorkCompletionModal = ({
  isOpen,
  onClose,
  ticket,
  onSubmit,
  userInfoData,
}) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const { expenseCategories } = useSelector((state) => state.hygiene);
  const { uploadToCloudfront, isUploading } = useCloudFrontUpload();

  // Form state
  const [resolutionType, setResolutionType] = useState("");
  const [workCompletedRemarks, setWorkCompletedRemarks] = useState("");
  const [proofFiles, setProofFiles] = useState({ images: [], videos: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Expense related state
  const [expenseCategory, setExpenseCategory] = useState("");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [payeeType, setPayeeType] = useState("");
  const [vendor, setVendor] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [upiId, setUpiId] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [invoiceImage, setInvoiceImage] = useState("");
  const [paymentMethodImage, setPaymentMethodImage] = useState("");

  // Data state
  const [allCategoriesData, setAllCategoriesData] = useState([]);
  const [allVendorsData, setAllVendorsData] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  // Fetch expense categories
  useEffect(() => {
    if (isOpen && token) {
      const filters = {
        page: 1,
        limit: 3000,
        status: "active",
        is_gym_expense: "1", // For gym expenses
      };

      dispatch(
        fetchExpenseCategoriesData({
          filters: filters,
          token: token,
        })
      );
    }
  }, [isOpen, token, dispatch]);

  // Format categories data
  useEffect(() => {
    if (expenseCategories?.status && expenseCategories?.data?.length > 0) {
      const formattedCategories = expenseCategories.data.map((item) => ({
        value: item.uid,
        label: item.name,
        info: item,
      }));
      setAllCategoriesData(formattedCategories);
    }
  }, [expenseCategories]);

  // Fetch vendors when modal opens
  useEffect(() => {
    if (isOpen && ticket?.gym_id && token) {
      fetchVendorsData();
    }
  }, [isOpen, ticket, token]);

  const fetchVendorsData = async () => {
    try {
      const filters = {
        page: 1,
        limit: 1000,
        status: "active",
        gym_id: ticket?.gym_id || "",
      };
      const vendors = await fetchVendors(filters, token);

      setAllVendorsData(vendors);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setAllVendorsData([]);
    }
  };

  // console.log("allVendorsData", allVendorsData);

  // Validation function
  const validateForm = () => {
    const errors = {};

    if (!resolutionType) {
      errors.resolutionType = "Please select resolution type";
    }

    if (!workCompletedRemarks || workCompletedRemarks.length < 20) {
      errors.workCompletedRemarks =
        "Work summary must be at least 20 characters";
    }

    if (proofFiles.images.length === 0 && proofFiles.videos.length === 0) {
      errors.proof = "Please upload at least one proof image or video";
    }

    if (resolutionType === "paid") {
      if (!expenseCategory) {
        errors.expenseCategory = "Please select expense category";
      }
      if (!requestedAmount || requestedAmount <= 0) {
        errors.requestedAmount = "Please enter valid amount";
      }
      if (!payeeType) {
        errors.payeeType = "Please select payee type";
      }
      if (payeeType === "vendor" && !vendor) {
        errors.vendor = "Please select vendor";
      }
      if (!paymentMethod) {
        errors.paymentMethod = "Please select payment method";
      }
      if (paymentMethod === "upi" && !upiId) {
        errors.upiId = "Please enter UPI ID";
      }
      if (paymentMethod === "bank" && (!ifscCode || !accountNumber)) {
        errors.bankDetails = "Please enter IFSC code and account number";
      }
      if (paymentMethod === "qr" && !paymentMethodImage) {
        errors.paymentMethodImage = "Please upload QR code image";
      }
      if (paymentMethod === "qr" && Number(requestedAmount || 0) > 2000) {
        errors.qrLimit = "QR payments are limited to ₹2000 maximum";
      }
      if (!invoiceImage) {
        errors.invoiceImage = "Please upload invoice/bill";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle proof upload
  const handleProofFilesChange = (files) => {
    setProofFiles(files);
  };

  // Handle invoice upload
  const handleInvoiceUpload = async (imageList) => {
    if (imageList.length > 0) {
      const file = imageList[0].file;
      if (file) {
        const uploadedUrl = await uploadToCloudfront(file, token);
        if (uploadedUrl) {
          setInvoiceImage(uploadedUrl);
        }
      }
    }
  };

  // Handle payment method image upload
  const handlePaymentMethodImageUpload = async (imageList) => {
    if (imageList.length > 0) {
      const file = imageList[0].file;
      if (file) {
        const uploadedUrl = await uploadToCloudfront(file, token);
        if (uploadedUrl) {
          setPaymentMethodImage(uploadedUrl);
        }
      }
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ticket_id: ticket?.uid,
        completed_by: userInfoData?.uid || userInfoData?.user_id,
        work_completed_remarks: workCompletedRemarks,
        resolution_type: resolutionType,
        imgArr: proofFiles.images,
        videoArr: proofFiles.videos,
      };

      // Add expense details if paid resolution
      if (resolutionType === "paid") {
        payload.requested_by = userInfoData?.uid || userInfoData?.user_id;
        payload.expense_category = expenseCategory;
        payload.expense_type = "gym";
        payload.requested_amount = requestedAmount;
        payload.requested_description = workCompletedRemarks;
        payload.payment_method = paymentMethod;
        payload.upi_id = upiId || "";
        payload.ifsc_code = ifscCode || "";
        payload.account_number = accountNumber || "";
        payload.payment_method_img = paymentMethodImage || "";
        payload.requested_img = invoiceImage;
        payload.payee_type = payeeType;
        payload.vendor_id = vendor || "";
      }

      const result = await dispatch(
        commonPostPutApi({
          method: "POST",
          endpoint: "/ticketSystem/complete",
          payload: payload,
          token: token,
        })
      );

      if (result?.payload?.status) {
        toast.success(result.payload.message || "Work completed successfully!");
        resetForm();
        onClose();
        if (onSubmit) {
          onSubmit();
        }
      } else {
        toast.error(result?.payload?.message || "Failed to complete work");
      }
    } catch (error) {
      console.error("Error completing work:", error);
      toast.error("An error occurred while completing work");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setResolutionType("");
    setWorkCompletedRemarks("");
    setProofFiles({ images: [], videos: [] });
    setExpenseCategory("");
    setRequestedAmount("");
    setPayeeType("");
    setVendor("");
    setPaymentMethod("");
    setUpiId("");
    setIfscCode("");
    setAccountNumber("");
    setInvoiceImage("");
    setPaymentMethodImage("");
    setFormErrors({});
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel=""
      ariaHideApp={false}
      closeTimeoutMS={300}
    >
      <div className="work-completion-modal-wrapper">
        <div className="work-completion-container">
          <ModalHeadAndCloseV2
            title={`Work Progress & Completion - ${
              ticket?.ticket_no || "TK000"
            }`}
            subtitle={`${ticket?.gym_name || "WTF Gym"} - ${
              ticket?.issue_type_name || ""
            } - ${ticket?.category_name || ""}`}
            onClose={onClose}
          />
          <br />

          <div className="modal-body-content">
            {/* Resolution Type Selection */}
            <div className="form-section">
              <label className="section-label required">
                RESOLUTION METHOD
              </label>
              <div
                className={`select-wrapper ${
                  formErrors.resolutionType ? "error" : ""
                }`}
              >
                <SelectDropdown
                  options={RESOLUTION_TYPES}
                  selectedOption={
                    resolutionType
                      ? RESOLUTION_TYPES.find((r) => r.value === resolutionType)
                      : ""
                  }
                  setSelectedOption={(option) =>
                    setResolutionType(option?.value || "")
                  }
                  name="Resolution Type"
                  preventKeyboardOpen={true}
                />
                {formErrors.resolutionType && (
                  <span className="error-message">
                    {formErrors.resolutionType}
                  </span>
                )}
              </div>
            </div>

            {/* Work Completed Remarks */}
            <div className="form-section">
              <label className="section-label required">
                WORK DONE SUMMARY
              </label>
              <div
                className={`textarea-wrapper ${
                  formErrors.workCompletedRemarks ? "error" : ""
                }`}
              >
                <ReactQuill
                  value={workCompletedRemarks}
                  onChange={setWorkCompletedRemarks}
                  placeholder="Describe the work completed, steps taken, and current status..."
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["clean"],
                    ],
                  }}
                />
                <div className="char-count">
                  {workCompletedRemarks.replace(/<[^>]*>/g, "").length}{" "}
                  characters
                </div>
                {formErrors.workCompletedRemarks && (
                  <span className="error-message">
                    {formErrors.workCompletedRemarks}
                  </span>
                )}
              </div>
            </div>

            {/* Paid External Service Details */}
            {resolutionType === "paid" && (
              <div className="expense-details-section">
                <h4 className="section-header">External Service Details</h4>

                <Row>
                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label required">
                        Amount Paid (₹)
                        {paymentMethod === "qr" && (
                          <span className="helper-text">
                            {" "}
                            (Max ₹2000 for QR)
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        className={`form-control ${
                          formErrors.requestedAmount || formErrors.qrLimit
                            ? "error"
                            : ""
                        }`}
                        placeholder="Enter amount"
                        value={requestedAmount}
                        onChange={(e) => setRequestedAmount(e.target.value)}
                        max={paymentMethod === "qr" ? 2000 : undefined}
                      />
                      {formErrors.requestedAmount && (
                        <span className="error-message">
                          {formErrors.requestedAmount}
                        </span>
                      )}
                      {formErrors.qrLimit && (
                        <span className="error-message">
                          {formErrors.qrLimit}
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label required">
                        Expense Category
                      </label>
                      <SelectDropdown
                        options={allCategoriesData}
                        selectedOption={
                          expenseCategory
                            ? allCategoriesData.find(
                                (c) => c.value === expenseCategory
                              )
                            : ""
                        }
                        setSelectedOption={(option) =>
                          setExpenseCategory(option?.value || "")
                        }
                        name="Category"
                      />
                      {formErrors.expenseCategory && (
                        <span className="error-message">
                          {formErrors.expenseCategory}
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label required">Payee Type</label>
                      <SelectDropdown
                        options={expense_payee_options}
                        selectedOption={
                          payeeType
                            ? expense_payee_options.find(
                                (p) => p.value === payeeType
                              )
                            : ""
                        }
                        setSelectedOption={(option) =>
                          setPayeeType(option?.value || "")
                        }
                        name="Payee"
                      />
                      {formErrors.payeeType && (
                        <span className="error-message">
                          {formErrors.payeeType}
                        </span>
                      )}
                    </div>
                  </Col>

                  {payeeType === "vendor" && (
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label required">
                          Vendor Name
                        </label>
                        <SelectDropdown
                          options={allVendorsData}
                          selectedOption={
                            vendor
                              ? allVendorsData.find((v) => v.value === vendor)
                              : ""
                          }
                          setSelectedOption={(option) =>
                            setVendor(option?.value || "")
                          }
                          name="Vendor"
                        />
                        {formErrors.vendor && (
                          <span className="error-message">
                            {formErrors.vendor}
                          </span>
                        )}
                      </div>
                    </Col>
                  )}

                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label required">
                        Mode of Payment
                      </label>
                      <SelectDropdown
                        options={expense_payment_options}
                        selectedOption={
                          paymentMethod
                            ? expense_payment_options.find(
                                (p) => p.value === paymentMethod
                              )
                            : ""
                        }
                        setSelectedOption={(option) =>
                          setPaymentMethod(option?.value || "")
                        }
                        name="Payment Method"
                      />
                      {formErrors.paymentMethod && (
                        <span className="error-message">
                          {formErrors.paymentMethod}
                        </span>
                      )}
                    </div>
                  </Col>

                  {paymentMethod === "upi" && (
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label required">UPI ID</label>
                        <input
                          type="text"
                          className={`form-control ${
                            formErrors.upiId ? "error" : ""
                          }`}
                          placeholder="Enter UPI ID"
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value)}
                        />
                        {formErrors.upiId && (
                          <span className="error-message">
                            {formErrors.upiId}
                          </span>
                        )}
                      </div>
                    </Col>
                  )}

                  {paymentMethod === "bank" && (
                    <>
                      <Col md={6}>
                        <div className="form-group">
                          <label className="form-label required">
                            IFSC Code
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              formErrors.bankDetails ? "error" : ""
                            }`}
                            placeholder="Enter IFSC Code"
                            value={ifscCode}
                            onChange={(e) => setIfscCode(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="form-group">
                          <label className="form-label required">
                            Account Number
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              formErrors.bankDetails ? "error" : ""
                            }`}
                            placeholder="Enter Account Number"
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value)}
                          />
                          {formErrors.bankDetails && (
                            <span className="error-message">
                              {formErrors.bankDetails}
                            </span>
                          )}
                        </div>
                      </Col>
                    </>
                  )}
                </Row>

                {/* Payment Method Image Upload */}
                {(paymentMethod === "qr" || paymentMethod === "bank") && (
                  <div className="upload-section">
                    <label className="section-label required">
                      Payment Method Image{" "}
                      {paymentMethod === "qr" && (
                        <span className="required-star">*</span>
                      )}
                    </label>
                    <p className="upload-hint">
                      {paymentMethod === "qr"
                        ? "Upload QR code screenshot"
                        : paymentMethod === "upi"
                        ? "Upload UPI details screenshot"
                        : "Upload bank details screenshot"}
                    </p>
                    <div
                      className={`upload-area ${
                        formErrors.paymentMethodImage ? "error" : ""
                      }`}
                    >
                      <ImageUploading
                        value={
                          paymentMethodImage
                            ? [{ data_url: paymentMethodImage }]
                            : []
                        }
                        onChange={handlePaymentMethodImageUpload}
                        maxNumber={1}
                        dataURLKey="data_url"
                        acceptType={["jpg", "jpeg", "png"]}
                      >
                        {({
                          imageList,
                          onImageUpload,
                          onImageRemove,
                          isDragging,
                          dragProps,
                        }) => (
                          <div className="upload-wrapper">
                            {imageList.length === 0 ? (
                              <div
                                className={`upload-placeholder ${
                                  isDragging ? "dragging" : ""
                                }`}
                                onClick={onImageUpload}
                                {...dragProps}
                              >
                                <CloudUploadIcon />
                                <p>
                                  Upload payment method image (Max 1 file, 10MB)
                                </p>
                                <small>Accepted formats: JPG, PNG</small>
                              </div>
                            ) : (
                              <div className="uploaded-file">
                                <span>Payment method image uploaded</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    onImageRemove(0);
                                    setPaymentMethodImage("");
                                  }}
                                  className="remove-btn"
                                >
                                  <DeleteIcon />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </ImageUploading>
                      {formErrors.paymentMethodImage && (
                        <span className="error-message">
                          {formErrors.paymentMethodImage}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Invoice/Bill Upload */}
                <div className="upload-section">
                  <label className="section-label required">
                    Invoice/Bill (PDF/Image)
                  </label>
                  <div
                    className={`upload-area ${
                      formErrors.invoiceImage ? "error" : ""
                    }`}
                  >
                    <ImageUploading
                      value={invoiceImage ? [{ data_url: invoiceImage }] : []}
                      onChange={handleInvoiceUpload}
                      maxNumber={1}
                      dataURLKey="data_url"
                      acceptType={["jpg", "jpeg", "png", "pdf"]}
                    >
                      {({
                        imageList,
                        onImageUpload,
                        onImageRemove,
                        isDragging,
                        dragProps,
                      }) => (
                        <div className="upload-wrapper">
                          {imageList.length === 0 ? (
                            <div
                              className={`upload-placeholder ${
                                isDragging ? "dragging" : ""
                              }`}
                              onClick={onImageUpload}
                              {...dragProps}
                            >
                              <CloudUploadIcon />
                              <p>Upload invoice/bill (Max 1 file, 10MB)</p>
                              <small>Accepted formats: JPG, PNG, PDF</small>
                            </div>
                          ) : (
                            <div className="uploaded-file">
                              <span>Invoice uploaded</span>
                              <button
                                type="button"
                                onClick={() => {
                                  onImageRemove(0);
                                  setInvoiceImage("");
                                }}
                                className="remove-btn"
                              >
                                <DeleteIcon />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </ImageUploading>
                    {formErrors.invoiceImage && (
                      <span className="error-message">
                        {formErrors.invoiceImage}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Proof Upload Section */}
            <div className="upload-section">
              <FileUploadComponent
                maxFiles={3}
                maxFileSize={50}
                acceptedFormats={["jpg", "jpeg", "png", "mp4"]}
                onFilesChange={handleProofFilesChange}
                label="PROOF IMAGE(S)/VIDEO"
                hint="Upload photos or videos showing the completed work"
                required={true}
                disabled={isSubmitting}
                className={formErrors.proof ? "error" : ""}
              />
              {formErrors.proof && (
                <span className="error-message">{formErrors.proof}</span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="modal-actions">
              <Button
                variant="outline-secondary"
                onClick={onClose}
                disabled={isSubmitting || isUploading}
              >
                Cancel Work
              </Button>
              <Button
                variant="danger"
                onClick={handleSubmit}
                disabled={isSubmitting || isUploading}
                className="complete-btn"
              >
                {isSubmitting || isUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Processing...
                  </>
                ) : (
                  "Mark as Work Completed"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WorkCompletionModal;
